import { Star } from 'lucide-react';

export interface PublicReviewSummary {
  averageRating: number;
  count: number;
  highlights: { author: string | null; rating: number; text: string }[];
}

function Stars({ rating, className }: { rating: number; className?: string }) {
  // Round to the nearest whole star for the filled/empty split — customers read
  // the precise figure from the numeral beside it, not by counting half-stars.
  const filled = Math.round(rating);
  return (
    <div className={`flex items-center gap-0.5 ${className ?? ''}`} aria-hidden="true">
      {Array.from({ length: 5 }, (_, i) => (
        <Star
          key={i}
          className={
            i < filled
              ? 'size-4 fill-[#D99A2B] text-[#D99A2B]'
              : 'size-4 fill-gray-200 text-gray-200'
          }
        />
      ))}
    </div>
  );
}

/**
 * Social proof for the single-scope estimate — the tenant's aggregate rating and
 * a few recent highlights, from reviews synced off Google/Facebook/etc. Renders
 * only when the server resolved a summary (count &gt; 0), so tenants with no
 * reviews show nothing rather than an empty "0 reviews" shell.
 */
export function SingleScopeReviews({ reviews }: { reviews?: PublicReviewSummary | null }) {
  if (!reviews || reviews.count <= 0) return null;

  const { averageRating, count, highlights } = reviews;
  const reviewWord = count === 1 ? 'review' : 'reviews';
  // The artifact leads with a single featured highlight as a pull-quote; the
  // rest inform the aggregate but aren't stacked as cards.
  const featured = highlights[0] ?? null;

  return (
    <div className="mt-6">
      {/* Proof band — a borderless inline row ruled top and bottom, not a card. */}
      <div className="flex flex-wrap items-center gap-x-[26px] gap-y-1 border-y border-[#14141A]/[0.08] py-4">
        <Stars rating={averageRating} />
        <p className="text-[13px] text-[#63646B]" aria-label={`${averageRating} out of 5 stars`}>
          <span className="text-[16px] font-medium text-[#17171A]">{averageRating.toFixed(1)}</span>{' '}
          · {count} Google {reviewWord}
        </p>
      </div>

      {/* Featured review — a left-accent-ruled pull-quote. */}
      {featured ? (
        <figure className="mt-6 border-l-2 border-[var(--ss-accent-soft)] pl-5">
          <blockquote className="text-[17.5px] leading-[1.62] text-[#33343A]">
            “{featured.text}”
          </blockquote>
          {featured.author ? (
            <figcaption className="mt-2 text-[13px] text-[#63646B]">— {featured.author}</figcaption>
          ) : null}
        </figure>
      ) : null}
    </div>
  );
}
